import React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from '../../config/apiConfig';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../router/authContext";

export const Navigation = (props) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const navigate = useNavigate();
  const { logout } = useAuth();
  
  useEffect(() => {
    axios.get(API_ENDPOINTS.user, {
      headers: {
        Authorization: `Token ${localStorage.getItem('authToken')}`
      }
    })
      .then(response => {
        setUserData(response.data);
        setIsAuthenticated(true);
      })
      .catch(error => {
        console.log("User is not authenticated", error);
        setIsAuthenticated(false);
      });
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img 
                src="../img/logo.png" 
                alt="AmpTrans logo" 
                className="h-10 w-auto max-h-10"
                style={{maxHeight: '40px'}}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-4">
              <a 
                href="#about" 
                className="font-nunito animated-underline text-gray-700 hover:text-gray-900 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-transparent uppercase"
              >
                About
              </a>
              <Link 
                to="/calculator" 
                className="font-nunito animated-underline text-gray-700 hover:text-gray-900 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-transparent uppercase"
              >
                Solutions
              </Link>
              
              {/* User Authentication Area */}
              {isAuthenticated ? (
                <div className="relative dropdown-container">
                  <button
                    onClick={toggleDropdown}
                    className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-200"
                  >
                    {userData?.first_name || 'User'}
                    <svg 
                      className={`ml-1.5 h-3 w-3 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-200">
                      <div className="py-1">
                        <Link
                          to="/profile"
                          onClick={() => {setIsDropdownOpen(false); setIsMobileMenuOpen(false);}}
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                        >
                          Profile
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                        >
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link 
                  to="/login" 
                  className="font-nunito btn-primary"
                >
                  Login
                </Link>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <span className="sr-only">Open main menu</span>
              {!isMobileMenuOpen ? (
                <svg className="block h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200">
          <div className="px-4 pt-3 pb-3 space-y-2 bg-white">
            <a 
              href="#about" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="block bg-white hover:bg-gray-200 text-gray-700 hover:text-gray-900 px-4 py-2 rounded-lg text-base font-medium transition-all duration-200"
            >
              About
            </a>
            <Link 
              to="/calculator"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block bg-white hover:bg-gray-200 text-gray-700 hover:text-gray-900 px-4 py-2 rounded-lg text-base font-medium transition-all duration-200"
            >
              Solutions
            </Link>
            
            {/* Mobile Authentication Area */}
            {isAuthenticated ? (
              <div className="pt-2 pb-2 border-t border-gray-200">
                <div className="flex items-center px-4 py-2 bg-gray-50 rounded-lg">
                  <div className="text-base font-medium text-gray-800">
                    {userData?.first_name || 'User'}
                  </div>
                </div>
                <div className="mt-2 space-y-2">
                  <Link
                    to="/profile"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block bg-white hover:bg-gray-200 text-gray-700 hover:text-gray-900 px-4 py-2 rounded-lg text-base font-medium transition-all duration-200"
                  >
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full bg-red-100 hover:bg-red-200 text-red-700 hover:text-red-800 px-4 py-2 rounded-lg text-base font-medium transition-all duration-200"
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="pt-2 pb-2 border-t border-gray-200">
                <Link 
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="btn-primary btn-full"
                >
                  Login
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
